import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BottomTabs } from "@/components/BottomTabs";
import { BrandHeader } from "@/components/BrandHeader";
import { ArrowDownLeft, ArrowUpRight, Loader2 } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { PendingVouchers } from "@/components/PendingVouchers";

interface Tx {
  id: string;
  sender_id: string;
  receiver_id: string;
  amount_cents: number;
  note: string | null;
  created_at: string;
}
interface Profile {
  display_name: string;
  cashtag: string;
}

const History = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [txs, setTxs] = useState<Tx[]>([]);
  const [counter, setCounter] = useState<Record<string, Profile>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("transactions")
        .select("*")
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .order("created_at", { ascending: false });
      const list = (data as Tx[] | null) ?? [];
      setTxs(list);

      const ids = Array.from(new Set(list.map((tx) => (tx.sender_id === user.id ? tx.receiver_id : tx.sender_id))));
      if (ids.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, display_name, cashtag")
          .in("user_id", ids);
        const map: Record<string, Profile> = {};
        (profs ?? []).forEach((p: any) => (map[p.user_id] = { display_name: p.display_name, cashtag: p.cashtag }));
        setCounter(map);
      }
      setLoading(false);
    })();
  }, [user]);

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Activity</h1>
      </BrandHeader>

      <div className="px-5 py-6 flex-1">
        <PendingVouchers />
        {loading ? (
          <div className="py-16 grid place-items-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : txs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center space-y-3">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-primary/10 grid place-items-center text-primary">
              <ArrowUpRight className="h-6 w-6" />
            </div>
            <p className="text-sm text-muted-foreground">No transactions yet.</p>
            <button
              onClick={() => navigate("/send")}
              className="text-sm font-semibold text-primary hover:underline"
            >
              Send your first payment →
            </button>
          </div>
        ) : (
          <ul className="space-y-2">
            {txs.map((tx) => {
              const isOut = tx.sender_id === user?.id;
              const other = counter[isOut ? tx.receiver_id : tx.sender_id];
              return (
                <li key={tx.id} className="flex items-center gap-3 bg-secondary/50 rounded-xl px-4 py-3">
                  <div className={`h-10 w-10 rounded-full grid place-items-center ${isOut ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}`}>
                    {isOut ? <ArrowUpRight className="h-5 w-5" /> : <ArrowDownLeft className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{isOut ? "To" : "From"} {other?.display_name ?? "Unknown"}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      ${other?.cashtag} · {new Date(tx.created_at).toLocaleString()}
                    </p>
                    {tx.note && <p className="text-xs text-muted-foreground mt-0.5 truncate">"{tx.note}"</p>}
                  </div>
                  <p className={`font-display font-bold ${isOut ? "text-foreground" : "text-success"}`}>
                    {isOut ? "-" : "+"}{formatMoney(tx.amount_cents)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <BottomTabs />
    </MobileShell>
  );
};

export default History;
